import './WishList.component.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ERoutes } from '../../../assets/constants/routes';
import { ENotification } from '../../../domain/notification.interface';
import {
  EUserType,
  ITypeaheadItem,
  ITypeaheadList,
  minQueryLength,
  optionContactConfig,
} from '../../../domain/typeahead.interfaces';
import {
  EDefaultWishList,
  EWishlistVisibility,
  IWish,
} from '../../../domain/wish.interface';
import { makeSelectCurrentUser } from '../../../store/current-user/current-user.selectors';
import { returnNotification } from '../../../store/render-controller/render-controller.actions';
import { getUsers, resetUsers } from '../../../store/users/users.actions';
import { makeSelectUsers } from '../../../store/users/users.selectors';
import {
  addWishListAuthUsers,
  copyWish,
  deleteWish,
  getWishListAuthUsers,
  loadWishList,
  patchStatusWish,
  removeWishListAuthUser,
} from '../../../store/wishlist/wishlist.actions';
import { makeSelectWishList } from '../../../store/wishlist/wishlist.selectors';
import { typeaheadItemFormatter } from '../../../utils/formatter';
import { replacePathParams } from '../../../utils/NavigateWithParams';
import { SpinnerComponent, WishCardComponent } from '../../atoms/atoms.index';
import {
  NavbarComponent,
  NavigationMenuComponent,
  WishListAuthModalFormComponent,
} from '../../organisms/organisms.index';
import { WishListShareComponent } from '../wishlist-share/WishListShare.component';
import { StorageKeys } from '../../../assets/constants/storage';
import ShareWishModalComponent from '../../organisms/share-wish-modal/ShareWishModal.component';

export function WishListComponent(props: any) {
  const dispatch = useDispatch();
  const wishListId = props.match.params.wishListId;
  const wishListReducer = useSelector(makeSelectWishList());
  const currentUser = useSelector(makeSelectCurrentUser());
  const users = useSelector(makeSelectUsers());
  const [wishList, setWishList] = useState(wishListReducer);
  const [readOnly, setReadOnly] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const isAuthenticated = currentUser && !!currentUser.id;
  const [query, setQuery] = useState<string | undefined>('');

  useEffect(() => {
    let isReadOnly =
      (props.location.state && props.location.state.readOnly) ||
      !isAuthenticated;
    if (
      !isAuthenticated &&
      wishList &&
      wishList.id &&
      localStorage.getItem(StorageKeys.session) === 'true'
    ) {
      auth();
    }
    if (wishList && wishList.id && !isReadOnly) {
      isReadOnly = wishList.user.id !== currentUser.id;
    }

    setReadOnly(isReadOnly);
    // eslint-disable-next-line
  }, [currentUser, wishList]);

  useEffect(() => {
    dispatch(loadWishList(wishListId));
  }, []);

  useEffect(() => {
    setWishList(wishListReducer);
  }, [wishListReducer]);

  function auth() {
    setRedirecting(true);
    const url = `${
      process.env.REACT_APP_BASE_URL
    }${ERoutes.CONTACT_WISH_LIST.replace(':wishListId', wishList.id)}`;
    window.location.href = url;
  }

  function deleteItem(id: string) {
    dispatch(
      returnNotification({
        type: ENotification.WARNING,
        msg1: 'warning.confirmation.message',
        dispatch: {
          func: deleteWish,
          param: [wishListId, id],
        },
      }),
    );
  }

  useEffect(() => {
    if (!query || query.trim().length < minQueryLength) {
      dispatch(resetUsers());
      return;
    }
    debouncedFetchUsers();
    // eslint-disable-next-line
  }, [query]);

  const handleInputChange = (pattern: string) => {
    setQuery(pattern.trim().toLowerCase());
  };

  const fetchUsers = () => {
    dispatch(getUsers(query as string));
    return true;
  };

  const debouncedFetchUsers = _.debounce(fetchUsers, 300, { maxWait: 1000 });

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authUsers, setAuthUsers] = useState<any[]>([]);
  const [selectedAuth, setSelectedAuth] = useState<ITypeaheadItem[]>([]);
  const [authRef, setAuthRef] = useState<any>(undefined);
  const [authOptions, setAuthOptions] = useState<
    ITypeaheadList[] | undefined
  >();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareWishId, setShareWishId] = useState('');

  useEffect(() => {
    const filteredUsers = users.filter(
      (el: any) =>
        !selectedAuth.some((user: ITypeaheadItem) => el.id === user.id) &&
        el.id !== currentUser.id,
    );
    const apiContacts = filteredUsers
      .filter((el: any) => el.isContact)
      .map((user: any) => {
        const userType = user.isActive ? EUserType.CONTACT : EUserType.INACTIVE;
        return typeaheadItemFormatter(optionContactConfig, user, userType);
      });
    const authOptionsConfig: ITypeaheadList[] = [
      {
        title: 'contacts',
        data: apiContacts,
      },
    ];
    setAuthOptions(authOptionsConfig);
    // eslint-disable-next-line
  }, [users, selectedAuth]);

  const handleAuthModalShow = async () => {
    await getAutorized();
    setShowAuthModal(true);
  };
  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    setAuthUsers([]);
    dispatch(resetUsers());
    authRef.getInstance().clear();
    setQuery('');
  };

  const handleOnBlurAuth = () => {
    dispatch(resetUsers());
    authRef.getInstance().clear();
    setQuery('');
  };

  function submitAuthForm() {
    if (!selectedAuth.length) {
      handleAuthModalClose();
      return;
    }
    const body = selectedAuth.map((user: ITypeaheadItem) => user.id);
    addWishListAuthUsers(body, wishListId);
    handleAuthModalClose();
  }

  const getAutorized = async () => {
    const authorized = await getWishListAuthUsers(wishListId);
    const formattedAuth = authorized.map((user: any) => {
      const userType = user.isActive ? EUserType.CONTACT : EUserType.INACTIVE;
      return typeaheadItemFormatter(optionContactConfig, user, userType);
    });
    setAuthUsers(formattedAuth);
  };

  const handleAddNewSelectedAuth = (authSelectedArray: ITypeaheadItem[]) => {
    setSelectedAuth(authSelectedArray);
  };

  async function removeAuthUser(user: ITypeaheadItem) {
    await removeWishListAuthUser(wishListId, user.id);
    getAutorized();
  }

  const handleShareModalShow = () => setShowShareModal(true);

  const handleShareModalClose = () => {
    setShowShareModal(false);
  };

  function shareItem(id: string) {
    setShareWishId(id);
    handleShareModalShow();
  }

  const modalCloseAndAuth = () => () => auth();

  function bookItem(wishId: string) {
    if (!isAuthenticated) {
      const notification = {
        type: ENotification.SUCCESS,
        msg1: 'pages.wishlist.public.invite.full',
        dispatch: { func: modalCloseAndAuth, param: [] },
      };

      dispatch(returnNotification(notification));
      return;
    }

    dispatch(patchStatusWish(wishListId, wishId));
  }

  function copyItem(wishId: string) {
    if (!isAuthenticated) {
      const notification = {
        type: ENotification.SUCCESS,
        msg1: 'pages.wishlist.public.invite.full',
        dispatch: { func: modalCloseAndAuth, param: [] },
      };

      dispatch(returnNotification(notification));
      return;
    }

    dispatch(copyWish(wishId));
  }

  function editItem(wish: IWish) {
    props.history.push(
      replacePathParams(ERoutes.WISH, {
        wishId: wish.id,
        wishListId,
      }),
    );
  }

  return (
    <>
      {!redirecting && (
        <div className='wish-list page-container'>
          <NavbarComponent
            hideBack={!isAuthenticated}
            noNavigateHome={!isAuthenticated}
          />
          {isAuthenticated && <NavigationMenuComponent {...props} />}
          {wishList && (
            <div>
              <div className='list-title-container d-flex'>
                <div className='list-title'>
                  <h2>
                    {wishList.isDefault ? (
                      <FormattedMessage
                        id={`pages.wishlist_catalogue.default_list_${
                          wishList.name === EDefaultWishList.MY_WISHES ? 1 : 2
                        }`}
                      />
                    ) : (
                      wishList.name
                    )}
                  </h2>
                  {wishList.visibility === EWishlistVisibility.PRIVATE &&
                    !readOnly && (
                      <button
                        className='btn-lili'
                        onClick={() => handleAuthModalShow()}
                      >
                        <FontAwesomeIcon icon='user-check' />
                        <FormattedMessage id='pages.wishlist.user_check' />
                      </button>
                    )}
                </div>
              </div>
              {readOnly && (
                <div className='wishlist-owner'>
                  <FormattedMessage id='pages.wishlist.userlist'>
                    {(txt: any) => (
                      <p>
                        <span className='text-primary-lg font-weight-light'>
                          {txt}
                          {': '}
                        </span>
                        <span className='text-secondary font-weight-light'>
                          <span className='name-to-show'>
                            {wishList.user && wishList.user.name}
                          </span>
                        </span>
                      </p>
                    )}
                  </FormattedMessage>
                </div>
              )}
              <div>
                <WishListShareComponent wishlist={wishList} />
              </div>
              <div className='fluid-container content-wrapper'>
                {readOnly ? null : wishList.isDefault &&
                  wishList.name === 'Recomendaciones' ? null : (
                  <Link
                    to={replacePathParams(ERoutes.NEW_WISH, {
                      wishListId: wishListId,
                    })}
                  >
                    <button className='add-btn mb-2'>
                      <FontAwesomeIcon icon='plus-circle' />{' '}
                      <FormattedMessage id='pages.wishlist.add_wish' />
                    </button>
                  </Link>
                )}
              </div>
              <div className='fluid-container content-wrapper pt-0'>
                <div className='wish-list-container'>
                  {wishList.wishes &&
                    wishList.wishes.map((wish: any) => (
                      <WishCardComponent
                        key={`wishCard-${wish.id}`}
                        wish={wish}
                        {...props}
                        deleteItem={deleteItem}
                        bookItem={bookItem}
                        shareItem={shareItem}
                        copyItem={copyItem}
                        editItem={editItem}
                        readOnly={readOnly}
                      />
                    ))}
                </div>
              </div>
            </div>
          )}
          <div>
            <WishListAuthModalFormComponent
              {...props}
              showModal={showAuthModal}
              handleClose={handleAuthModalClose}
              authUsers={authUsers}
              removeAuthUser={removeAuthUser}
              options={authOptions}
              addRef={(r: any) => setAuthRef(r)}
              handleInputChange={handleInputChange}
              handleOnBlur={handleOnBlurAuth}
              handleAddNewSelected={handleAddNewSelectedAuth}
              submitSelected={submitAuthForm}
            />
            <ShareWishModalComponent
              wishId={shareWishId}
              wishListId={wishListId}
              showModal={showShareModal}
              handleClose={handleShareModalClose}
            />
          </div>
        </div>
      )}
      {redirecting && <SpinnerComponent />}
    </>
  );
}
